import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { WorkspaceProvider, useWorkspace } from './WorkspaceContext';
import { MODE_IDS } from './tokens';
import { Atelier } from './atelier/Atelier';
import './workspace.css';

/**
 * UnifiedWorkspace - entry point. Hosts <WorkspaceProvider> + <Atelier />.
 *
 *   /workspace          → default (chat-leaning)
 *   /workspace/{mode}   → mode = chat | image | video | music (sets initial draft mode)
 *
 * The whole UI lives in <Atelier />: pure white surfaces, violet accents,
 * unified thread per project with auto-detected modality.
 */
export function UnifiedWorkspace() {
  const { mode: routeMode } = useParams();
  const initial = MODE_IDS.includes(routeMode) ? routeMode : 'chat';

  return (
    <WorkspaceProvider initialMode={initial}>
      <RouteSync>
        <Atelier />
      </RouteSync>
    </WorkspaceProvider>
  );
}

/** Keeps URL ↔ activeMode in sync (kept for shareable mode links). */
function RouteSync({ children }) {
  const { activeMode, switchMode, goHome } = useWorkspace();
  const navigate = useNavigate();
  const { mode: routeMode } = useParams();

  // On first mount, if we are at the root /workspace without a mode,
  // we can keep the Bento home shell. But if we are in a mode,
  // we should stay there.
  useEffect(() => {
    // Only go home if there is no active project and we are at the root
    if (!activeMode && !routeMode && goHome) {
      goHome();
    }
  }, []);

  useEffect(() => {
    if (activeMode !== routeMode) {
      navigate(`/workspace/${activeMode}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode]);

  useEffect(() => {
    if (routeMode && MODE_IDS.includes(routeMode) && routeMode !== activeMode) {
      switchMode(routeMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMode]);

  return children;
}

export default UnifiedWorkspace;
